/**
 * `$web_vitals` event shaping for the attribution build of `web-vitals`
 * (draft 8 §6 row 0 — measure which interactions dominate marketing INP).
 *
 * PRIVACY. This module only ever emits an allow-listed set of numbers, enums
 * and two derived strings:
 *   - `route_template`: the Next.js route pattern (`/blog/[slug]`), never the
 *     concrete path. Dynamic segments come from the router's own params; any
 *     segment the params did not account for that still looks like an
 *     identifier is masked as well.
 *   - `interaction_target`: web-vitals' CSS selector for the element the user
 *     touched (tag, id and class names only, capped at 100 characters), with
 *     identifier-like runs masked.
 * Resource URLs (`lcp` `url`, script `sourceURL`, entries) are NOT forwarded.
 * A metric whose interaction happened on a suppressed (token-bearing or
 * private) surface is dropped entirely, even if it is reported after the
 * visitor has navigated away from it. Consent and the live-URL checks stay in
 * the PostHog `before_send` chokepoint.
 */
import type {
  CLSMetricWithAttribution,
  INPMetricWithAttribution,
  LCPMetricWithAttribution,
} from "web-vitals/attribution";

export type AttributedWebVitalsMetric =
  | CLSMetricWithAttribution
  | INPMetricWithAttribution
  | LCPMetricWithAttribution;

export type RouteParams = Readonly<
  Record<string, string | readonly string[] | undefined>
>;

/** One SPA route the page was on, from `at` (performance.now()) onwards. */
export interface RouteMark {
  at: number;
  /** Concrete pathname — used only to attach the template, never sent. */
  path: string;
  /** The route pattern, or null while unknown. Unknown is treated as suppressed. */
  template: string | null;
  suppressed: boolean;
}

const MAX_SELECTOR_LENGTH = 100;
const MAX_ROUTE_MARKS = 50;
// A run of 16+ word characters that contains a digit reads as an identifier
// (UUID, cuid, token, numeric id) rather than a Tailwind class or tag name.
const IDENTIFIER_RUN = /[A-Za-z0-9_-]{16,}/g;

function maskIdentifierRuns(value: string): string {
  return value.replace(IDENTIFIER_RUN, (run) => (/\d/.test(run) ? "*" : run));
}

// An element id is free-form (it can carry a record id or a slug with no
// digits), so any long run in an `#id` part is masked, digits or not.
function maskSelectorPart(part: string): string {
  return part.startsWith("#")
    ? part.replace(IDENTIFIER_RUN, "*")
    : maskIdentifierRuns(part);
}

/**
 * Append the route the page moved to. The timeline is bounded: the oldest
 * mark after the first is merged INTO its predecessor, never dropped — the
 * merged span stays suppressed if either part was, and becomes unknown (so
 * also not sent) if their templates differ.
 */
export function recordRouteMark(marks: RouteMark[], mark: RouteMark): void {
  const last = marks[marks.length - 1];
  if (
    last &&
    last.path === mark.path &&
    last.suppressed === mark.suppressed &&
    (mark.template === null || last.template === mark.template)
  ) {
    return;
  }
  marks.push(mark);
  while (marks.length > MAX_ROUTE_MARKS) {
    const [first, evicted] = marks;
    marks.splice(0, 2, {
      at: first!.at,
      path: first!.path,
      template: first!.template === evicted!.template ? first!.template : null,
      suppressed: first!.suppressed || evicted!.suppressed,
    });
  }
}

/** Attach the route pattern to the latest mark for `path` still missing one. */
export function annotateRouteTemplate(
  marks: RouteMark[],
  path: string,
  template: string,
): boolean {
  for (let index = marks.length - 1; index >= 0; index -= 1) {
    const mark = marks[index]!;
    if (mark.path !== path) continue;
    if (mark.template === null) mark.template = template;
    return true;
  }
  return false;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Map a concrete pathname back to its route pattern using the router params:
 * `/blog/how-to-write-rams` + `{ slug: "how-to-write-rams" }` →
 * `/blog/[slug]`. Catch-all params become `[...name]`. Matching runs from the
 * right so a static segment that happens to equal a param value is kept.
 */
export function routeTemplateFor(
  pathname: string,
  params: RouteParams | null | undefined,
): string {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  const segments = path.split("/");
  const owned = segments.map(() => false);
  for (const [name, value] of Object.entries(params ?? {})) {
    if (value === undefined) continue;
    if (typeof value === "string") {
      for (let index = segments.length - 1; index >= 1; index -= 1) {
        if (!owned[index] && decodeSegment(segments[index]!) === value) {
          segments[index] = `[${name}]`;
          owned[index] = true;
          break;
        }
      }
      continue;
    }
    if (value.length === 0) continue;
    for (
      let start = segments.length - value.length;
      start >= 1;
      start -= 1
    ) {
      const matches = value.every(
        (part, offset) =>
          !owned[start + offset] &&
          decodeSegment(segments[start + offset]!) === part,
      );
      if (!matches) continue;
      segments.splice(start, value.length, `[...${name}]`);
      owned.splice(start, value.length, true);
      break;
    }
  }
  const template = segments
    .map((segment, index) => (owned[index] ? segment : maskIdentifierRuns(segment)))
    .join("/");
  return template || "/";
}

/**
 * web-vitals' interaction selector, with identifier-like runs masked. Masked
 * BEFORE truncation, so a cut can never leave the visible half of an id.
 */
export function scrubInteractionTarget(
  selector: string | undefined,
): string | undefined {
  if (!selector) return undefined;
  return selector
    .split(">")
    .map(maskSelectorPart)
    .join(">")
    .slice(0, MAX_SELECTOR_LENGTH);
}

/** The page time a metric describes, so it is attributed to the right route. */
export function metricTime(metric: AttributedWebVitalsMetric): number | undefined {
  switch (metric.name) {
    case "INP":
      return metric.attribution.interactionTime;
    case "LCP":
      return metric.attribution.lcpEntry?.startTime;
    case "CLS":
      return metric.attribution.largestShiftTime;
  }
}

/** The route the page was on at `time` (the latest mark at or before it). */
export function routeAt(
  marks: readonly RouteMark[],
  time: number | undefined,
): RouteMark | undefined {
  if (marks.length === 0) return undefined;
  if (time === undefined) return marks[marks.length - 1];
  let current = marks[0];
  for (const mark of marks) {
    if (mark.at > time) break;
    current = mark;
  }
  return current;
}

function roundMs(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Allow-listed `$web_vitals` properties, or `null` when the metric belongs to
 * a suppressed surface (or no route is known) and must not be sent at all.
 */
export function webVitalsEventProperties(
  metric: AttributedWebVitalsMetric,
  route: RouteMark | undefined,
): Record<string, string | number> | null {
  // Unknown route (template never attached, or a merged span of different
  // routes) is treated as suppressed: nothing about it is sent.
  if (!route || route.suppressed || route.template === null) return null;
  const properties: Record<string, string | number> = {
    metric: metric.name,
    value: metric.value,
    rating: metric.rating,
    route_template: route.template,
  };
  if (metric.name === "INP") {
    const { attribution } = metric;
    const target = scrubInteractionTarget(attribution.interactionTarget);
    if (target) properties.interaction_target = target;
    if (attribution.interactionType) {
      properties.interaction_type = attribution.interactionType;
    }
    properties.input_delay = roundMs(attribution.inputDelay);
    properties.processing_duration = roundMs(attribution.processingDuration);
    properties.presentation_delay = roundMs(attribution.presentationDelay);
    properties.load_state = attribution.loadState;
  } else if (metric.name === "CLS" && metric.attribution.loadState) {
    properties.load_state = metric.attribution.loadState;
  }
  return properties;
}
